// Transcript outline — renders a tick (and matching popover list item) per turn from
// the live data published by massimo--message-visibility, highlights the current turn,
// and jumps to a turn on click. Because it rebuilds from that data, turns added while
// chatting appear automatically. Hidden until there are at least two turns.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["card", "ticks", "list", "tickTemplate", "itemTemplate"];

  #signature = null;
  #activeId = null;

  onVisibilityChange({ detail }) {
    if (!this.hasCardTarget) return;
    const { turns, currentAnchorId } = detail;

    if (turns.length < 2) {
      this.cardTarget.classList.add("hidden");
      this.#signature = null;
      this.#activeId = null;
      return;
    }
    this.cardTarget.classList.remove("hidden");

    // The event fires on every scroll frame; rebuild/highlight only on a real change.
    // Turns only grow or change at the tail, so count + last id is a sufficient signature.
    const signature = `${turns.length}:${turns[turns.length - 1].id}`;
    const rebuilt = signature !== this.#signature;
    if (rebuilt) {
      this.#signature = signature;
      this.#rebuild(turns);
    }
    if (rebuilt || currentAnchorId !== this.#activeId) {
      this.#activeId = currentAnchorId;
      this.#highlight(currentAnchorId);
    }
  }

  // Ask auto-scroll to jump to the clicked turn (decoupled via a window event).
  jumpTo(event) {
    const id = event.currentTarget.dataset.messageId;
    this.dispatch("scroll-to-message", { prefix: "massimo", target: window, detail: { id } });
  }

  #rebuild(turns) {
    this.ticksTarget.replaceChildren(
      ...turns.map((turn) => {
        const tick = this.tickTemplateTarget.content.firstElementChild.cloneNode(true);
        tick.dataset.messageId = turn.id;
        return tick;
      })
    );
    this.listTarget.replaceChildren(
      ...turns.map((turn) => {
        const item = this.itemTemplateTarget.content.firstElementChild.cloneNode(true);
        item.dataset.messageId = turn.id;
        item.textContent = turn.label;
        return item;
      })
    );
  }

  // Only called when the active turn changes, so the scrollIntoView that keeps the active
  // tick visible in a long (scrolling) rail fires on change rather than every frame.
  #highlight(activeId) {
    this.#markActive(this.ticksTarget.children, activeId, true);
    this.#markActive(this.listTarget.children, activeId, false);
  }

  #markActive(children, activeId, scrollActiveIntoView) {
    for (const el of children) {
      const active = el.dataset.messageId === activeId;
      el.toggleAttribute("data-active", active);
      if (active && scrollActiveIntoView) el.scrollIntoView({ block: "nearest" });
    }
  }
}
